import { useIntersectionObserver } from '@/core/hooks/useIntersectionObserver';
import { LAZY_LOAD_OBSERVER_OPTIONS } from '@/core/config/intersectionObserver';
import { AnalyticsBannerLink } from '@/shared/features/analytics/presentation/components';

interface FadedBannerSectionProps {
  data: Record<string, unknown> & {
    title?: string[];
    showTitle?: boolean;
    url?: string;
    textImageUrl?: string;
    link?: string;
    isVideo?: boolean;
    posterUrl?: string;
    autoplay?: string;
    playsinline?: string;
    loop?: string;
    muted?: string;
    controls?: string;
    lazyload?: string;
    blankImageUrl?: string;
    shouldRefresh?: boolean;
  };
  sectionKey: string;
  eventLabel?: string;
}

export default function FadedBannerSection({
  data,
  sectionKey: _sectionKey,
  eventLabel: _eventLabel,
}: FadedBannerSectionProps) {
  const title = (data.title || []) as string[];
  const showTitle = data.showTitle === true;
  const url = data.url || '';
  const textImageUrl = data.textImageUrl;
  const link = data.link || '#';
  const isVideo = data.isVideo === true;
  const posterUrl = data.posterUrl;
  const autoplay = data.autoplay;
  const playsinline = data.playsinline;
  const loop = data.loop;
  const muted = data.muted;
  const controls = data.controls;
  const lazyload = data.lazyload;

  const shouldAutoplay = !!autoplay;
  // iOS Safari autoplay requirements: muted + playsInline.
  // Also, controls can interfere with autoplay UX; disable controls when autoplaying.
  const shouldMuted = !!muted || shouldAutoplay;
  const shouldPlaysInline = !!playsinline || shouldAutoplay;
  const shouldLoop = !!loop || shouldAutoplay;
  const shouldControls = !!controls && !shouldAutoplay;

  const { ref: videoRef } = useIntersectionObserver<HTMLVideoElement>(
    { ...LAZY_LOAD_OBSERVER_OPTIONS, enabled: isVideo && !!url },
    (entry) => {
      if (entry.isIntersecting) {
        const videoEl = entry.target as HTMLVideoElement;
        videoEl.load();

        if (shouldAutoplay) {
          // On iPad/iOS, explicitly calling play() improves reliability vs relying on autoplay alone.
          const p = videoEl.play();
          if (p) p.catch(() => {});
        }
      } else if (shouldAutoplay) {
        (entry.target as HTMLVideoElement).pause();
      }
    }
  );

  const ariaLabel = title.length ? title.join(' ') : 'Faded Banner';

  if (!url) {
    return null;
  }

  const loadingMode = lazyload ? 'lazy' : 'eager';

  const MediaContent = isVideo ? (
    <video
      ref={videoRef}
      poster={posterUrl}
      className="w-full h-auto block"
      preload={lazyload ? 'none' : 'auto'}
      autoPlay={shouldAutoplay}
      playsInline={shouldPlaysInline}
      loop={shouldLoop}
      muted={shouldMuted}
      controls={shouldControls}
    >
      <source src={url} type="video/mp4" />
      Your browser does not support the video tag.
    </video>
  ) : (
    <img
      src={url}
      alt={ariaLabel}
      className="w-full h-auto block"
      loading={loadingMode}
      decoding="async"
    />
  );

  const TextOverlay = textImageUrl ? (
    <img
      src={textImageUrl}
      alt=""
      loading={loadingMode}
      decoding="async"
    />
  ) : null;

  const Clickable = (
    <AnalyticsBannerLink
      to={link}
      bannerName={ariaLabel}
      bannerType="banner"
      fromNavBar={false}
      className="absolute inset-0 z-10"
      title={title[0] || 'Banner'}
      aria-label={ariaLabel}
      shouldRefresh={data.shouldRefresh}
    />
  );

  return (
    <section className="md:hidden">
      {showTitle && title.length > 0 && (
        <h2 className="mb-4 text-center text-base md:text-lg font-primary text-[#032C5E]">
          {title[0]}
          {title[1] ? (
            <>
              <br />
              {title[1]}
            </>
          ) : null}
        </h2>
      )}

      <div className="relative before:content-[''] before:[box-shadow:inset_0px_0_20px_10px_rgba(255,255,255,1)] before:absolute before:top-0 before:left-0 before:w-full before:h-full before:block before:z-[1] after:content-[''] after:[box-shadow:inset_60px_0_50px_60px_rgba(255,255,255,1)] after:absolute after:top-0 after:left-0 after:w-full after:h-full after:block after:z-[1]">
        {MediaContent}
        {TextOverlay && (
          <div className="absolute top-1/2 left-[10%] -translate-y-1/2 z-[2] w-[30%] h-auto pointer-events-none">
            {TextOverlay}
          </div>
        )}
        {Clickable}
      </div>
    </section>
  );
}

